export type AuthError = {
  message: string
  code: string
}

export const AUTH_ERROR_CODES = {
  USER_NOT_FOUND: 'USER_NOT_FOUND',
  USER_NOT_EXIST: 'USER_NOT_EXIST',
  INVALID_CREDENTIALS: 'INVALID_CREDENTIALS',
  AUTH_FAILED: 'AUTH_FAILED',
  USER_ALREADY_EXISTS: 'USER_ALREADY_EXISTS',
  USER_ALREADY_ACTIVATED: 'USER_ALREADY_ACTIVATED',
  INVALID_EMAIL: 'INVALID_EMAIL',
  WEAK_PASSWORD: 'WEAK_PASSWORD',
  ACTIVATION_EXPIRED: 'ACTIVATION_EXPIRED',
  ACTIVATION_INVALID: 'ACTIVATION_INVALID',
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  METHOD_ARGUMENT_NOT_VALID: 'METHOD_ARGUMENT_NOT_VALID',
  UNKNOWN: 'UNKNOWN',
  WEBEID_EXTENSION_UNAVAILABLE: 'WEBEID_EXTENSION_UNAVAILABLE',
  WEBEID_NATIVE_UNAVAILABLE: 'WEBEID_NATIVE_UNAVAILABLE',
  WEBEID_USER_CANCELLED: 'WEBEID_USER_CANCELLED',
  WEBEID_USER_TIMEOUT: 'WEBEID_USER_TIMEOUT',
  WEBEID_CERT_EXPIRED: 'WEBEID_CERT_EXPIRED',
  WEBEID_CONTEXT_INSECURE: 'WEBEID_CONTEXT_INSECURE',
  WEBEID_ACTION_PENDING: 'WEBEID_ACTION_PENDING',
  WEBEID_NATIVE_FATAL: 'WEBEID_NATIVE_FATAL',
  WEBEID_VERSION_MISMATCH: 'WEBEID_VERSION_MISMATCH',
  WEBEID_VERIFY_FAILED: 'WEBEID_VERIFY_FAILED',
  CHALLENGE_EXPIRED: 'CHALLENGE_EXPIRED',
  MOBILE_ID_CANCELLED_BY_USER: 'MOBILE_ID_CANCELLED_BY_USER',
  MOBILE_ID_TIMEOUT: 'MOBILE_ID_TIMEOUT',
  MOBILE_ID_INVALID_PERSONAL_IDENTIFICATION_NUMBER: 'MOBILE_ID_INVALID_PERSONAL_IDENTIFICATION_NUMBER',
  MOBILE_ID_INVALID_PHONE_NUMBER: 'MOBILE_ID_INVALID_PHONE_NUMBER',
  MOBILE_ID_NOT_MOBILE_ID_CLIENT: 'MOBILE_ID_NOT_MOBILE_ID_CLIENT',
  MOBILE_ID_NOT_A_CLIENT_OR_REVOKED: 'MOBILE_ID_NOT_A_CLIENT_OR_REVOKED',
} as const

export const AUTH_ERROR_MESSAGES = {
  INVALID_EMAIL_OR_PASSWORD: 'Invalid email or password',
  AUTH_ERROR: 'Auth error',
  SIGNUP_ERROR: 'Signup error',
  ACTIVATION_ERROR: 'Activation error',
  ACTIVATION_INVALID: 'Invalid activation link',
  ACTIVATION_EXPIRED_OR_INVALID: 'Activation link expired or invalid',
  UNKNOWN_ERROR: 'Unknown error',
} as const

// ── Request types ──
export type SignInParams = {
  email: string
  password: string
}

export type LogInParams = {
  body: SignInParams
  method?: 'email' | 'emailOrPhone'
  rememberMe?: boolean
}

export type SignUpEmailParams = {
  email: string
  password: string
  locale?: string
}

// ── Response types ──
export type SignUpResponse = {
  accountActivationCode: string
  userName: string
}

export type SignInResponse = {
  email: string
  phone: UserPhone | null
  registrationMethod: string
  status: string
  token: string
  userId: number
  userName: string
}

export type UserPhone = {
  intlCode: string
  phoneNumber: string
}

export type ActivateResponse = {
  token: string
  status: string
  userId: number
  userName: string
  email: string
}

export type PasswordResetPerformResponse = Omit<SignInResponse, 'token'>

// ── Smart-ID types ──
export type SmartIdCountry = 'EE' | 'LV' | 'LT'

export type SmartIdPrepareRequest = {
  personalIdentificationNumber: string
  country: SmartIdCountry
}

export type SmartIdPrepareResponse = {
  request: { country: string; personalIdentificationNumber: string }
  sessionId: string
  verificationCode: string
}

export type SmartIdSessionResult = 'OK' | 'USER_REFUSED' | 'TIMEOUT' | 'DOCUMENT_UNUSABLE' | 'WRONG_VC'

export type SmartIdPerformResponse = {
  accessToken: string
  accountNonExpired: boolean
  accountNonLocked: boolean
  credentialsNonExpired: boolean
  enabled: boolean
  permissions: string[]
  username: string
}

export type SmartIdSessionStatusResponse =
  | {
      state: 'RUNNING'
      result?: SmartIdSessionResult
      message?: string
    }
  | ({
      state: 'COMPLETE'
      result: SmartIdSessionResult
      message?: string
    } & Partial<SmartIdPerformResponse>)
  | {
      state: string
      result?: SmartIdSessionResult | string
      message?: string
    }

// ── Mobile-ID types ──
export type MobileIdPrepareRequest = {
  personalIdentificationNumber: string
  phoneNumber: string
}

export type MobileIdAuthenticationHash = {
  hash: string
  hashInBase64: string
  hashType: string
}

export type MobileIdPrepareResponse = {
  verificationCode: string
  authenticationHash: MobileIdAuthenticationHash
  personalIdentificationNumber: string
  phoneNumber: string
}

export type MobileIdProcessRequest = {
  personalIdentificationNumber: string
  phoneNumber: string
  authenticationHash: MobileIdAuthenticationHash
}

export type MobileIdAccountRole = {
  code: string
  permissions: string[]
  title: string
}

export type MobileIdAccount = {
  createdAt: string
  email: string
  enabled: boolean
  firstName: string
  lastName: string
  phone: UserPhone | null
  registrationMethod: string
  roles: MobileIdAccountRole[]
  status: string
  token: string
  userId: number
  userName: string
}

export type MobileIdProcessResponse = {
  account?: MobileIdAccount
}
